import * as sentry from '@sentry/react';

import {config} from 'config/env';
import {allowAlcohol, enable1PX, enableBrandDetails, enableSellInPiece} from 'constants/recommendation';
import {BasketRecommendationRequest, Product, RecommendationResponse} from 'types';
import {getProductAvailability, isProductInStock} from 'utils';
import {get} from 'utils/api';
import {productGraphqlRequest} from 'utils/graphqlRequests';
import {getIdFromGraphId} from 'utils/recommendation-utils/recommendations';

import {makeProductDetailByIdsQueryV2} from './constants/queries';

export const getBasketRecommendations = async (
  payload: BasketRecommendationRequest,
  idToken: string,
  userIdOrAnonymousId: number | string,
  isNewEndPoint: boolean,
  recommendationVariant: string,
  storeCodes: string[] | undefined,
  isExpress: boolean
) => {
  try {
    const encodedFilter = encodeURIComponent(`expressDelivery:[${storeCodes}]`);
    const newPayload = isExpress ? {...payload, filters: encodedFilter} : {...payload};

    let apiEndPoint = isNewEndPoint
      ? `/recommendations/USER_BASED/${recommendationVariant}`
      : '/product/user-recommendations';

    if (userIdOrAnonymousId) {
      apiEndPoint = `${apiEndPoint}/${userIdOrAnonymousId}`;
    }
    const endPointURL = new URL(`${config.RECOMMENDATION_API_ENDPOINT}${apiEndPoint}`).toString();

    const data: RecommendationResponse = await get(endPointURL, false, true, false, idToken, false, newPayload);

    return data;
  } catch (error: any) {
    sentry.captureException(error);
    return {
      products: [],
      algorithm: '',
      recommendationType: '',
      recommendationVariant: '',
      totalCount: 0,
      position: 0
    };
  }
};

export async function getProductsByIds(
  ids: Array<string>,
  storeCodes?: string[],
  lang?: string,
  isSalesCustomer?: boolean,
  enableRmsStatus?: boolean,
  is3P?: boolean,
  membershipId?: string,
  shouldFetchDeliveryDescription?: boolean,
  isSameDayAllowedForStore?: boolean,
  date?: string,
  isProPointsEnabled?: boolean,
  isUnavailableReasonEnabled?: boolean,
  isExpressDeliveryStore?: boolean,
  is3PComplexPromotionEnabled?: boolean,
  _idToken?: string
): Promise<Product[]> {
  const response = await productGraphqlRequest(
    makeProductDetailByIdsQueryV2({
      enableRmsStatus,
      enableUltraFresh: isSalesCustomer,
      enableSellInPiece: enableSellInPiece,
      shouldFetchDeliveryDescription,
      enable1PX: enable1PX,
      enableBrandDetails: enableBrandDetails,
      isProPointsEnabled: isProPointsEnabled,
      isUnavailableReasonEnabled,
      is3PComplexPromotionEnabled
    }),
    {
      ids: ids.map((id) => (id.match(/^\d+$/) ? id : getIdFromGraphId(id))),
      storeCodes,
      lang,
      isSalesCustomer,
      is3P,
      allowAlcohol,
      membershipId,
      isSameDayAllowedForStore,
      date,
      isExpressDeliveryStore
    }
  );
  return response.products as Product[];
}

export const getRecommendationPieceResultV2 = (recommendProducts: Product[], product: Product[]): Product[] => {
  const bSortedProducts = recommendProducts.map((item: Product) => {
    const itemID = item.id;
    const productTemp = product.find((prod) => {
      return prod?.id === itemID;
    });
    return {
      ...productTemp,
      alreadyPurchased: item?.alreadyPurchased,
      recommendationScore: item?.recommendationScore,
      normalisedRecommendationScore: item?.normalisedRecommendationScore
    } as Product;
  });
  const actualResp = bSortedProducts.filter((p) => {
    return isProductInStock(p as any) && getProductAvailability(p as any, []).isAvailable;
  });
  return actualResp;
};
